import {
	H2,
	HorizontalGroup,
	Input,
	P,
	Space as FormattedSpace,
	VerticalGroup,
} from "@saysom/shared"
import { nanoid } from "nanoid"
import { GetServerSideProps } from "next"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import Body from "../../../components/body/body"
import ContinueButton from "../../../components/buttons/continueButton"
import DirectionSwitch from "../../../components/directionSwitch"
import CantileverGroup from "../../../components/landing/cantileverGroup"
import { fetchApi, fetchApiAuthorized } from "../../../network/fetch"
import { useAreaStore } from "../../../stores/areaStore"
import { authFromStore, useAuthStore } from "../../../stores/authStore"
import { Label } from "../../../styles"
import { Flowy } from "../../../styles/space_style"
import { APP_URL } from "../../../utils/baseUrl"

const SpacePreview = dynamic(
	() => import("../../../components/spacePreview/spacePreview"),
	{
		ssr: false,
	}
)

const Space = ({ space }: { space: FormattedSpace }) => {
	const router = useRouter()
	const { code } = router.query
	const auth = authFromStore()

	const [name, setName] = useState("")
	const [email, setEmail] = useState("")
	const [newsletter, setNewsletter] = useState(false)

	const [user, setUser] = useState<Record<string, unknown> | undefined>(
		undefined
	)

	useEffect(() => {
		if (
			auth.refreshExpiresIn &&
			new Date().getTime() / 1000 < auth.refreshExpiresIn
		) {
			fetchApiAuthorized("/users/", auth)
				.then((_user) => {
					setUser(_user)
				})
				.catch((e) => console.log(e))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth.refreshExpiresIn])

	useEffect(() => {
		if (user) {
			setName(user.name as string)
			setEmail(user.email as string)
		}
	}, [user])

	useEffect(() => {
		useAreaStore.setState({ areas: {} })

		if (space && space.areas) {
			space.areas.forEach((area) => {
				useAreaStore.getState().updateArea.add(area.key, area)
			})
		}
	}, [space])

	return (
		<Body
			className="justify-center"
			subtitle="Space"
			style={{
				background:
					space && space.colorScheme && space.colorScheme.roomColor
						? `linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.8) 47.4%, rgba(255, 255, 255, 0) 100%), linear-gradient(58.22deg, ${space.colorScheme.roomColor[0]} 0%, ${space.colorScheme.roomColor[1]} 100%)`
						: "linear-gradient(180deg, white 0%, rgba(255, 255, 255, 0.8) 47.4%, rgba(255, 255, 255, 0) 100%), linear-gradient(58.22deg, #521AFF 0%, #7E1AFF 16.31%, #DF738E 79.5%, #F9BAA9 97.85%)",
			}}
		>
			<div className="flex flex-1 flex-col justify-center">
				<DirectionSwitch className="bg-white bg-opacity-50 rounded-xl py-10 justify-self-center">
					<CantileverGroup className="self-center">
						<SpacePreview space={space} />
					</CantileverGroup>

					<VerticalGroup
						flex={1}
						className="space-y-2.5 p-2.5 pr-8 justify-items-center"
					>
						<H2
							style={{
								flex: "0",
								backgroundImage: Flowy,
								WebkitBackgroundClip: "text",
								WebkitTextFillColor: "transparent",
								whiteSpace: "nowrap",
								alignSelf: "flex-start",
							}}
							className="mb-4"
						>
							{space ? space.title : ""}
						</H2>

						<Label>
							Name
							<Input
								placeholder="Will be displayed on your avatar"
								type="text"
								value={name}
								onChange={(value) => setName(value)}
								required
							/>
						</Label>
						<Label>
							Email
							<Input
								placeholder="Email (optional)"
								type="email"
								value={email}
								onChange={(value) => setEmail(value)}
								required
							/>
						</Label>
						<HorizontalGroup
							alignItems="center"
							spacing={10}
							alignSelf="center"
							style={{ marginBottom: "30px" }}
						>
							<input
								type="checkbox"
								id="newsletter"
								name="newsletter"
								disabled={email.length === 0}
								checked={newsletter}
								onChange={() => {
									setNewsletter((old) => !old)
								}}
							/>
							<label htmlFor="newsletter">Signup for newsletter</label>
						</HorizontalGroup>

						<ContinueButton
							className="py-3 px-4 text-base self-center"
							disabled={name.length === 0 || space.isOver}
							onClick={async () => {
								const result = await fetchApi(
									`/spaces/join/${code}`,
									{
										name,
										token: useAuthStore.getState().token,
										email: email ?? nanoid(),
										newsletter,
									},
									"POST"
								)

								if (result && result.token) {
									const { token, expiresIn } = result

									window.location.href = `${APP_URL}/${code}/join?token=${token}&expiresIn=${expiresIn}`
								}
							}}
							text={space.isOver ? "This space is over" : "Join"}
							isBlack
						/>
					</VerticalGroup>
				</DirectionSwitch>
			</div>
		</Body>
	)
}

export default Space

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { code } = context.query

	const space = await fetchApi(`/spaces/${code}`)
	if (space) {
		return { props: { space: space } }
	}

	return {
		notFound: true,
	}
}
